import type { Bordereau, Mouvement } from '@mairistem/comptabilite';
import type { TemplateRequestGet } from '@mairistem/finances-utils';
import { request } from '@mairistem/tools';
import type DuplicatePayload from '../../../interfaces/pieces/DuplicatePayload';
import type { InfoChorus } from '../../../utils/chorus/afficherInfoChorus';
import type { AvertissementApi } from '../../../utils/disponible';

const baseUrl = '/api/finances/comptabilite/piecescomptables/mouvements';

const getMouvements = (params: URLSearchParams) =>
	request.get<unknown, TemplateRequestGet<Mouvement>>(
		`${baseUrl}?${params.toString()}`,
	);

export const dupliquerMouvement = (
	mouvement: Partial<Mouvement>,
	shouldReprendreReferences?: boolean,
	bordereauIsModel?: boolean,
	dateEcheance?: string,
	dateDepartDelaiGlobalPaiement?: string,
	datePrelevement?: string,
	dateRealisationPrestationDebut?: string,
	dateRealisationPrestationFin?: string,
	bordereau?: Partial<Bordereau>,
) => {
	const parameters: DuplicatePayload = {
		idBordereau: mouvement.identifiantBordereau,
		shouldReprendreReferences,
		willBeModel: bordereauIsModel,
		dateEcheance,
		dateDepartDelaiGlobalPaiement,
		datePrelevement,
		dateRealisationPrestationDebut,
		dateRealisationPrestationFin,
		bordereau,
	};

	const url = `${baseUrl}/dupliquer/${mouvement.identifiant}`;
	return request.post<
		DuplicatePayload,
		Mouvement & { warnings?: AvertissementApi[] }
	>(url, parameters);
};

export interface AnnulationRejetMouvement {
	/** Bordereau en brouillard qui reçoit la pièce : d'annulation (02) ou de rejet (RE). */
	identifiantBordereau: number;
	/** Vrai : annulation (« Annulation du… ») ; faux : rejet (« Rejet du… »). */
	willBeAnnulation: boolean;
	/** Le montant annulé redevient disponible sur l'engagement ou la facture repris. */
	reengager: boolean;
}

/**
 * Annule ou rejette une pièce historisée : la pièce d'annulation ou de rejet, qui reprend le reste à annuler de
 * chaque ligne, est créée dans le bordereau donné et renvoyée.
 */
export const annulerRejeterMouvement = (
	identifiant: number,
	annulationRejet: AnnulationRejetMouvement,
) =>
	request.post<AnnulationRejetMouvement, Mouvement>(
		`${baseUrl}/annulerejet/${identifiant}`,
		annulationRejet,
	);

/**
 * Supprime une pièce : mandats issus de factures Chorus concernés en retour (InfoChorus). L'API répond 200 avec ce
 * corps : request.custom, car request.delete n'accepte que 204 ; il résout la réponse entière.
 */
export const supprimerMouvement = async (
	identifiant: number,
): Promise<InfoChorus> => {
	const reponse = await request.custom<unknown, { data?: InfoChorus }>(
		'delete',
		`${baseUrl}/${identifiant}`,
	);
	return reponse?.data || {};
};

export default getMouvements;
